import { useDispatch, useSelector } from "react-redux";
import AdminHeader from "../../../Components/AdminHeader/AdminHeader";
import styles from "./Dashboard.module.scss";
import { AppDispatch, RootState } from "../../../Redux/store";
import { useEffect, useState } from "react";
import {
  getFeedbackChartByNumber,
  getStats,
} from "../../../Redux/Stats/StatsActions";
import InlineLoader from "../../../Components/InlineLoader/InlineLoader";
import { DateTime } from "luxon";
import FeedbackChartByNumber from "../../../Components/FeedbackChartByNumber/FeedbackChartByNumber";
import React from "react";

const Dashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    allstats_loading,
    allstats_data,
    allstats_error,
    feedback_chart_by_number_data,
    feedback_chart_by_number_error,
    feedback_chart_by_number_loading,
  } = useSelector((state: RootState) => state.stats);

  // Date filter state
  const [selectedFilter, setSelectedFilter] = useState<string>("");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [showCustomDateInputs, setShowCustomDateInputs] =
    useState<boolean>(false);

  const [search, setSearch] = useState<string>("");
  // Date filter options
  const dateFilterOptions = [
    { value: "today", label: "Today" },
    { value: "lastWeek", label: "Last Week" },
    { value: "lastMonth", label: "Last Month" },
    { value: "last3Months", label: "Last 3 Months" },
    { value: "lastYear", label: "Last Year" },
    { value: "custom", label: "Custom Range" },
  ];

  const filteredByJobNumber = feedback_chart_by_number_data?.data?.filter(
    (item: any) => {
      return (
        item?.job?.name?.toLowerCase().includes(search.toLowerCase()) ||
        item?.job?.address?.toLowerCase().includes(search.toLowerCase()) ||
        item?.job?.contact_number_mobile?.includes(search) ||
        item?.job?.contact_number_phone?.includes(search) ||
        item?.job?.email_home?.toLowerCase().includes(search.toLowerCase()) ||
        item?.job?.email_work?.toLowerCase().includes(search.toLowerCase()) ||
        item?.job?.jobType?.toLowerCase().includes(search.toLowerCase()) ||
        item?.job?.claimNumber?.includes(search) ||
        item?.job?.number?.includes(search)
      );
    }
  );

  // Function to get date range based on selected filter
  const getDateRange = () => {
    const today = DateTime.now().setZone("America/New_York").startOf("day");
    const todayEnd = today.endOf("day");

    switch (selectedFilter) {
      case "today":
        return {
          startDate: today.toJSDate(),
          endDate: todayEnd.toJSDate(),
        };
      case "lastWeek":
        return {
          startDate: today.minus({ days: 7 }).startOf("day").toJSDate(),
          endDate: todayEnd.toJSDate(),
        };
      case "lastMonth":
        return {
          startDate: today.minus({ months: 1 }).startOf("day").toJSDate(),
          endDate: todayEnd.toJSDate(),
        };
      case "last3Months":
        return {
          startDate: today.minus({ months: 3 }).startOf("day").toJSDate(),
          endDate: todayEnd.toJSDate(),
        };
      case "lastYear":
        return {
          startDate: today.minus({ years: 1 }).startOf("day").toJSDate(),
          endDate: todayEnd.toJSDate(),
        };
      case "custom":
        return {
          startDate: customStartDate
            ? DateTime.fromISO(customStartDate, { zone: "America/New_York" })
                .startOf("day")
                .toJSDate()
            : null,
          endDate: customEndDate
            ? DateTime.fromISO(customEndDate, { zone: "America/New_York" })
                .endOf("day")
                .toJSDate()
            : null,
        };
      default:
        return {
          startDate: today.minus({ months: 1 }).startOf("day").toJSDate(),
          endDate: todayEnd.toJSDate(),
        };
    }
  };

  const handleFilterChange = (filterValue: string) => {
    setSelectedFilter(filterValue);
    if (filterValue === "custom") {
      setShowCustomDateInputs(true);
    } else {
      setShowCustomDateInputs(false);
    }
  };

  const handleCustomDateChange = (type: "start" | "end", value: string) => {
    if (type === "start") {
      setCustomStartDate(value);
    } else {
      setCustomEndDate(value);
    }
  };

  const applyFilters = () => {
    const dateRange = getDateRange();

    dispatch(
      getFeedbackChartByNumber({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      })
    );
  };

  useEffect(() => {
    dispatch(getStats());

    dispatch(getFeedbackChartByNumber());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters when selection changes
  useEffect(() => {
    if (
      selectedFilter !== "" &&
      (selectedFilter !== "custom" || (customStartDate && customEndDate))
    ) {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, customStartDate, customEndDate]);

  return (
    <div className={styles.mainContainer}>
      <AdminHeader />
      <div className={styles.bodyContainer}>
        <div className={styles.mainHeadingContainer}>
          <h1 className={styles.heading}>Statistics</h1>
        </div>
        {allstats_data && (
          <>
            <div className={styles.headingContainer}>
              <h1 className={styles.heading}>Text Message</h1>
            </div>
            <div className={styles.statsContainer}>
              <div className={styles.stats}>
                <p className={styles.label}>Today</p>
                <p className={styles.value}>
                  {allstats_data?.data?.text?.today}
                </p>
              </div>
              <div className={styles.stats}>
                <p className={styles.label}>Last 30 days</p>
                <p className={styles.value}>
                  {allstats_data?.data?.text?.month}
                </p>
              </div>
            </div>
          </>
        )}

        {allstats_data && (
          <>
            <div className={styles.headingContainer}>
              <h1 className={styles.heading}>Feedback</h1>
            </div>

            <div className={styles.statsContainer}>
              <div className={styles.stats}>
                <p className={styles.label}>Positive Today</p>
                <p className={styles.greenValue}>
                  {allstats_data?.data?.feedback?.today?.positive}
                </p>
              </div>
              <div className={styles.stats}>
                <p className={styles.label}>Positive Last 30 days</p>
                <p className={styles.greenValue}>
                  {allstats_data?.data?.feedback?.month?.positive}
                </p>
              </div>
              <div className={styles.stats}>
                <p className={styles.label}>Negative Today</p>
                <p className={styles.redValue}>
                  {allstats_data?.data?.feedback?.today?.negative}
                </p>
              </div>
              <div className={styles.stats}>
                <p className={styles.label}>Negative Last 30 days</p>
                <p className={styles.redValue}>
                  {allstats_data?.data?.feedback?.month?.negative}
                </p>
              </div>
            </div>
          </>
        )}

        {allstats_loading && (
          <div className={styles.loadingContainer}>
            <InlineLoader color="#000000" />
          </div>
        )}
        {allstats_error && (
          <div className={styles.errorContainer}>
            <p>
              {allstats_error?.message
                ? allstats_error?.message
                : "An error occured while getting data"}
            </p>
          </div>
        )}

        <div className={styles.headingContainer}>
          <h1 className={styles.heading}>Charts</h1>
        </div>

        {/* Date Filters */}
        <div className={styles.filtersContainer}>
          <div className={styles.filterButtons}>
            {dateFilterOptions.map((option) => (
              <button
                key={option.value}
                className={`${styles.filterButton} ${
                  selectedFilter === option.value ? styles.active : ""
                }`}
                onClick={() => handleFilterChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {showCustomDateInputs && (
            <div className={styles.customDateContainer}>
              <div className={styles.dateInputGroup}>
                <label className={styles.dateLabel}>Start Date:</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) =>
                    handleCustomDateChange("start", e.target.value)
                  }
                  className={styles.dateInput}
                />
              </div>
              <div className={styles.dateInputGroup}>
                <label className={styles.dateLabel}>End Date:</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) =>
                    handleCustomDateChange("end", e.target.value)
                  }
                  className={styles.dateInput}
                />
              </div>
            </div>
          )}
        </div>

        {feedback_chart_by_number_data?.data?.length === 0 && (
          <div className={styles.errorContainer}>
            <p>No Data Available</p>
          </div>
        )}
        <div className={styles.chartsContainer}>
          <div className={styles.subHeadingContainer}>
            <p className={styles.heading}>Chart By Job Number</p>
          </div>

          <div className={styles.searchContainer}>
            <input
              className={styles.input}
              type="text"
              placeholder="Search by Job Number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {filteredByJobNumber?.length > 0 &&
            filteredByJobNumber?.map((item: any, i: number) => {
              return (
                <React.Fragment key={i}>
                  <FeedbackChartByNumber data={item} />
                </React.Fragment>
              );
            })}

          {feedback_chart_by_number_loading && (
            <div className={styles.loadingContainer}>
              <InlineLoader color="#000000" />
            </div>
          )}
          {feedback_chart_by_number_error && (
            <div className={styles.errorContainer}>
              <p>
                {feedback_chart_by_number_error?.message
                  ? feedback_chart_by_number_error?.message
                  : "An error occured while getting data"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
